import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { SsSelectTooltipDirective } from '../../shared/ss-select-tooltip.directive';

import { DvmBuilderStepBase, TileRecomputeResult } from '../../dvm-layout-builder/dvm-builder-host';
import type { DashboardTile } from '../../dvm-layout-builder/dvm-layout-builder.types';
import { toKpiNumberFormat, toNumberScale } from '../../dvm-layout-builder/dvm-builder-number-format.utils';
import { buildHbWidgetData, hbAffixesFromFormat } from './horizontal-bar-widget.utils';

/**
 * Horizontal Bar build form (HORIZONTAL_BAR_CHART): one group-by column
 * (Y axis) + one formula-driven value (X axis), with optional per-group
 * formula overrides and typed references. Computed by the backend; the shell
 * runs the default Generate flow (POST → tile from response).
 */
@Component({
  selector: 'app-horizontal-bar-step',
  standalone: true,
  imports: [CommonModule, FormsModule, NgSelectModule, SsSelectTooltipDirective],
  templateUrl: './horizontal-bar-step.component.html',
  styles: [':host { display: contents; }'],
})
export class HorizontalBarStepComponent extends DvmBuilderStepBase {
  readonly layoutTypes = ['HORIZONTAL_BAR_CHART'] as const;

  hbGroupByField = '';
  hbGroupByLabel = '';
  hbValueLabel = 'Value';
  hbValueFormula = '';
  hbGroupFormulas: Array<{ group: string; formula: string }> = [];

  addHbGroupFormula(): void {
    this.hbGroupFormulas = [...this.hbGroupFormulas, { group: '', formula: '' }];
  }

  removeHbGroupFormula(entry: { group: string; formula: string }): void {
    this.hbGroupFormulas = this.hbGroupFormulas.filter((item) => item !== entry);
  }

  onHorizontalBarGroupFieldDrop(event: DragEvent): void {
    const mapping = this.host.readFieldDrop(event);
    if (!mapping) return;
    this.host.useDroppedSingleDataset(mapping.dataset);
    this.hbGroupByField = mapping.field;
    if (!this.hbGroupByLabel) this.hbGroupByLabel = mapping.field;
    this.host.invalidatePreview();
  }

  onHorizontalBarValueFieldDrop(event: DragEvent): void {
    const mapping = this.host.readFieldDrop(event);
    if (!mapping) return;
    this.host.useDroppedSingleDataset(mapping.dataset);
    this.hbValueFormula = this.host.appendFormulaToken(this.hbValueFormula, mapping.field);
    if (!this.hbValueLabel || this.hbValueLabel === 'Value') this.hbValueLabel = mapping.field;
    this.host.invalidatePreview();
  }

  private get hasGroupFormula(): boolean {
    return this.hbGroupFormulas.some((entry) => entry.group.trim() && entry.formula.trim());
  }

  // ── DvmBuilderStep ──────────────────────────────────────────────────────

  blockers(): string[] {
    if (!this.host.selectedDatasets.length) return ['Dataset'];
    const missing: string[] = [];
    if (!this.hbGroupByField) missing.push('Group by column');
    if (!this.hbValueFormula.trim() && !this.hasGroupFormula) missing.push('Value formula (or one group formula)');
    return missing;
  }

  buildPayload(): Record<string, unknown> {
    const hbFormula = this.hbValueFormula.trim();
    const hbReferences = this.host.referencesForFormula(this.host.componentId, hbFormula);
    const groupFormulaEntries = this.hbGroupFormulas
      .map((entry) => ({ group: entry.group.trim(), formula: entry.formula.trim() }))
      .filter((entry) => entry.group && entry.formula);
    const groupFormulas = groupFormulaEntries.length
      ? Object.fromEntries(groupFormulaEntries.map((entry) => [entry.group, entry.formula]))
      : undefined;
    const hbFilters = groupFormulaEntries.length && !hbFormula
      ? [{ dataset: this.host.drillDataset, field: this.hbGroupByField, operator: 'IN', value: groupFormulaEntries.map((entry) => entry.group) }]
      : undefined;
    return {
      componentId: this.host.componentId,
      dashboardId: this.host.dashboardId,
      componentType: 'CHART',
      chartType: this.host.selectedLayoutType,
      title: this.host.title.trim(),
      dataset: this.host.drillDataset,
      groupBy: {
        field: this.hbGroupByField,
        label: this.hbGroupByLabel || this.hbGroupByField,
      },
      value: {
        label: this.hbValueLabel || 'Value',
        ...(hbFormula ? { formula: hbFormula } : {}),
        ...(groupFormulas ? { groupFormulas } : {}),
        ...(hbReferences ? { references: hbReferences } : {}),
      },
      ...this.host.chartTargetsPayload(),
      ...(hbFilters ? { filters: hbFilters } : {}),
      ...this.host.headerFormatPayload(),
    };
  }

  loadFromTile(tile: DashboardTile, payload: Record<string, unknown>): void {
    const groupBy = (payload['groupBy'] as Record<string, unknown>) || {};
    const value = (payload['value'] as Record<string, unknown>) || {};
    this.hbGroupByField = String(groupBy['field'] ?? '');
    this.hbGroupByLabel = String(groupBy['label'] ?? '');
    this.hbValueLabel = String(value['label'] ?? 'Value');
    this.hbValueFormula = String(value['formula'] ?? '');
    const groupFormulas = (value['groupFormulas'] as Record<string, unknown>) || {};
    this.hbGroupFormulas = Object.keys(groupFormulas).map((group) => ({ group, formula: String(groupFormulas[group] ?? '') }));
    this.host.rehydrateOwnFormulaReferences(this.host.componentId, value['references']);
    this.host.barFormat = { ...(tile.format || {}) };
  }

  reset(): void {
    this.hbGroupByField = '';
    this.hbGroupByLabel = '';
    this.hbValueLabel = 'Value';
    this.hbValueFormula = '';
    this.hbGroupFormulas = [];
  }

  /** Widget data for a freshly generated tile (the shell adds it to the canvas). */
  buildWidgetData(response: Record<string, unknown> | null, tileId: string, format?: Record<string, unknown>): Record<string, unknown> {
    return buildHbWidgetData(response, {
      tileId,
      title: this.host.title,
      format,
      yAxisName: this.hbGroupByLabel,
      xAxisName: this.hbValueLabel,
    });
  }

  // ── Tile-side hooks (DvmBuilderStep) ──────────────────────────────────────

  tileFromResponse(base: DashboardTile, response: Record<string, unknown> | null): DashboardTile {
    return { ...base, hbData: this.buildWidgetData(response, base.id, base.format) };
  }

  applyFormatToTile(tile: DashboardTile, format: Record<string, unknown>): void {
    if (!tile.hbData) return;
    tile.hbData = {
      ...tile.hbData,
      title: (format['title'] as string) || tile.title,
      valueLabelPosition: format['showValues'] === false ? 'none' : 'inside',
      color: format['positiveColor'] || (tile.hbData['color'] as string),
      barColor: format['positiveColor'] || (tile.hbData['barColor'] as string),
      numberFormat: toKpiNumberFormat(format),
      numberScale: toNumberScale(format['numberFormat']),
      decimals: Number(format['decimals'] ?? 1),
      ...hbAffixesFromFormat(format),
    };
  }

  applyRecomputeResult(tile: DashboardTile, { result }: TileRecomputeResult): boolean {
    const hbData = buildHbWidgetData(result, {
      tileId: tile.id,
      title: tile.title,
      format: tile.format,
      yAxisName: String((tile.payload['groupBy'] as Record<string, unknown> | undefined)?.['label'] ?? ''),
      xAxisName: String((tile.payload['value'] as Record<string, unknown> | undefined)?.['label'] ?? 'Value'),
    });
    hbData['title'] = tile.title;
    tile.hbData = hbData;
    return true;
  }
}
